/**
 * Pestañas del panel inferior que leen el período (RR-28): «Informe» (las posiciones con los campos
 * de WOX, un selector de columnas y el estado del motor), «Eventos de telemetría» y «Alertas de
 * dispositivo».
 *
 * La tabla se desplaza DENTRO de su contenedor: la página nunca se va de lado.
 * Un dato que el equipo no mandó dice «sin dato», nunca 0 (constitución, Art. 3).
 */

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { RastreoEventsResponse, RastreoHealth, RastreoTrackResponse } from "@core/contracts/rastreo.ts";
import { RASTREO_MAX_PERIOD_DAYS } from "@core/contracts/rastreo.ts";
import {
  POSITION_FIELDS,
  toPositionRecords,
  type PositionFieldKey,
  type RastreoRow,
} from "@core/rastreo/registro.ts";
import type { AddAlertRuleInput } from "@core/contracts/rastreo-alertas.ts";
import { ApiError } from "@web/lib/api-client.ts";
import { Notice } from "@web/components/primitives.tsx";
import { ago, ALERT_CATALOG, alertGuidance, bogotaTime, formatPositionField, isCatalogCode } from "./rastreo-format.ts";
import { buildOccurrences, frameParameters, isNotified, type AlertRulesApi } from "./alert-rules.ts";
import { ColumnsIcon } from "./rastreo-icons.tsx";

export type PeriodControls = {
  from: string;
  to: string;
  today: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  rangeIsBackwards: boolean;
  tooLong: boolean;
};

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : "Error inesperado.");

/** Las fechas del período, en una fila compacta. Las comparten Informe, Eventos y Alertas. */
export function PeriodBar({ dates }: { dates: PeriodControls }) {
  const uid = useId();
  const invalid = dates.rangeIsBackwards || dates.tooLong;
  return (
    <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
      <div className="min-w-0">
        <label className="gd-label" htmlFor={`${uid}-desde`}>
          Desde
        </label>
        <input id={`${uid}-desde`} type="date" value={dates.from} max={dates.today} onChange={(e) => dates.onFrom(e.target.value)} />
      </div>
      <div className="min-w-0">
        <label className="gd-label" htmlFor={`${uid}-hasta`}>
          Hasta
        </label>
        <input
          id={`${uid}-hasta`}
          type="date"
          value={dates.to}
          min={dates.from}
          max={dates.today}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${uid}-hasta-error` : undefined}
          onChange={(e) => dates.onTo(e.target.value)}
        />
      </div>
      {invalid ? (
        <p id={`${uid}-hasta-error`} className="basis-full text-[13px] font-medium text-red-700">
          {dates.rangeIsBackwards
            ? "La fecha «desde» es posterior a la fecha «hasta»."
            : `El motor entrega como máximo ${RASTREO_MAX_PERIOD_DAYS} días por consulta. Acorta las fechas.`}
        </p>
      ) : null}
    </div>
  );
}

/** Lo que cada pestaña dice cuando no hay nada que mostrar, sea por falta de enlace o por fechas. */
function Unavailable({ row, dates }: { row: RastreoRow; dates: PeriodControls }) {
  if (!row.vehicle) {
    return (
      <div className="p-4">
        <Notice title="Este equipo no tiene datos del motor">
          {row.equipo && !row.equipo.motorDeviceId
            ? "Este equipo no quedó registrado en el motor de rastreo. Ábrelo en «Editar datos» y guarda otra vez para registrarlo."
            : "El motor no está reportando este equipo en este momento."}
        </Notice>
      </div>
    );
  }
  if (dates.rangeIsBackwards || dates.tooLong) return <p className="p-4 text-[14px] text-gd-muted">Corrige las fechas para ver el período.</p>;
  return null;
}

export function ReportTab({
  row,
  dates,
  track,
  health,
  now,
  columns,
  onColumns,
  alarms,
}: {
  row: RastreoRow;
  dates: PeriodControls;
  track: UseQueryResult<RastreoTrackResponse>;
  health: RastreoHealth | null;
  now: number;
  /** `false`: el equipo no mandó alarmas en el período, y las columnas de alarma se esconden. */
  alarms: boolean | null;
  /** Las columnas marcadas viven en el panel: cambiar de pestaña no las pierde. */
  columns: PositionFieldKey[];
  onColumns: (columns: PositionFieldKey[]) => void;
}) {
  const uid = useId();
  const [chooserOpen, setChooserOpen] = useState(false);

  // El selector de columnas se cierra con Escape.
  useEffect(() => {
    if (!chooserOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setChooserOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chooserOpen]);

  const records = useMemo(() => (track.data ? toPositionRecords(track.data.points) : []), [track.data]);

  // Las columnas salen en el orden de Traccar, no en el orden en que se marcaron.
  const fields = POSITION_FIELDS.filter((field) => alarms !== false || !ALARM_COLUMNS.has(field.key));
  const visible = fields.filter((field) => columns.includes(field.key));
  const toggle = (key: PositionFieldKey) =>
    onColumns(columns.includes(key) ? columns.filter((item) => item !== key) : [...columns, key]);

  const blocked = Unavailable({ row, dates });
  const columnButton = (
    <button
      type="button"
      onClick={() => setChooserOpen((open) => !open)}
      aria-expanded={chooserOpen}
      aria-controls={`${uid}-columnas`}
      className="gd-btn-secondary min-h-9 px-3 text-[13px]"
    >
      <ColumnsIcon className="size-4" /> Columnas ({visible.length})
    </button>
  );
  // El selector va en el flujo, entre el encabezado y la tabla: empuja la tabla en vez de flotar y
  // quedar recortado por el panel. Tiene su propio desplazamiento.
  const columnPanel = chooserOpen ? (
    <fieldset
      id={`${uid}-columnas`}
      className="m-0 grid max-h-40 gap-x-4 overflow-y-auto border-b border-gd-line bg-gd-mist px-3 py-2 sm:grid-cols-2 xl:grid-cols-3"
    >
      <legend className="sr-only">Columnas visibles</legend>
      {fields.map((field) => (
        <label key={field.key} className="flex cursor-pointer items-start gap-2 rounded-lg px-1 py-1.5 hover:bg-white">
          <input
            type="checkbox"
            checked={columns.includes(field.key)}
            onChange={() => toggle(field.key)}
            className="mt-1 size-4 shrink-0 accent-[var(--color-gd-blue)]"
          />
          <span className="min-w-0">
            <span className="block text-[13px] font-medium text-gd-navy">{field.label}</span>
            <span className="block text-[12px] text-gd-muted">
              {field.sent ? field.hint : `${field.hint} Saldrá «sin dato».`}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  ) : null;

  return (
    <div>
      {blocked ?? <PositionsTable title={row.name} records={records} visible={visible} track={track} chooser={columnButton} panel={columnPanel} />}
      {health ? <MotorHealth health={health} now={now} /> : null}
    </div>
  );
}

/** Columnas que solo tienen sentido en un GPS que manda alarmas. */
const ALARM_COLUMNS: ReadonlySet<PositionFieldKey> = new Set(["event", "geofence"]);

/**
 * ¿El equipo mandó alarmas en el período? Decide solo las COLUMNAS de alarma del informe (un GPS sin
 * alarmas no las muestra). Se mira lo que registró el motor y las posiciones de tipo alarma; cualquier
 * alarma cuenta, también la desconexión de energía. `null`: todavía no se sabe y no se esconde nada.
 * La pestaña «Alertas de dispositivo» ya no depende de esto: es donde se configuran las alertas y
 * tiene que poder abrirse también en un equipo que aún no mandó ninguna.
 */
export function periodHasAlarms(
  events: UseQueryResult<RastreoEventsResponse>,
  track: UseQueryResult<RastreoTrackResponse>,
): boolean | null {
  if ((events.data?.events.length ?? 0) > 0) return true;
  if ((track.data?.points ?? []).some((point) => typeof point.attributes["alarm"] === "string" || point.kind === "alarm")) return true;
  return events.isPending || events.isError || track.isPending || track.isError ? null : false;
}

function Status({ loading, error }: { loading: boolean; error: unknown }) {
  if (loading)
    return (
      <p className="text-[14px] text-gd-muted" role="status">
        Cargando…
      </p>
    );
  if (error)
    return (
      <p className="text-[14px] text-amber-900" role="alert">
        {errorText(error)}
      </p>
    );
  return null;
}

/**
 * Los eventos de telemetría que se quieren vigilar. `code` es la alarma del GPS que lo cubre; sin
 * `code`, el GT06 no manda ese evento y no se inventa a partir de la velocidad (Art. 3).
 */
const TELEMETRY_EVENTS: ReadonlyArray<{ name: string; code: string | null }> = [
  { name: "Frenada brusca", code: null },
  { name: "Aceleración brusca", code: null },
  { name: "Giro brusco", code: null },
  { name: "Desconexión de energía", code: "corte_energia" },
  { name: "Remolcado", code: null },
  { name: "Posible evento de colisión", code: null },
];

/** Eventos de telemetría: los seis que se vigilan y, abajo, las alertas con el aviso encendido. */
export function TelemetryTab({
  row,
  dates,
  events,
  track,
  rules,
}: {
  row: RastreoRow;
  dates: PeriodControls;
  events: UseQueryResult<RastreoEventsResponse>;
  track: UseQueryResult<RastreoTrackResponse>;
  rules: AlertRulesApi;
}) {
  const blocked = Unavailable({ row, dates });
  if (blocked) return blocked;
  const all = buildOccurrences(events.data, track.data?.points, rules.rules);
  const power = all.filter((item) => item.telemetry);
  const notified = all.filter((item) => !item.telemetry && isNotified(item.code, rules.rules));
  const ready = !events.isPending && !events.isError;
  // «Alertas avisadas» sale de eventos, posiciones y avisos guardados: no se dice «Ninguna» hasta tenerlos
  // (o saber que fallaron), porque faltando una fuente la lista parecería completa sin serlo.
  const alertsReady = ready && !track.isPending && !rules.query.isPending;
  const cut = events.data?.truncated === true || track.data?.truncated === true;
  // Un recorrido que no llegó o que falló deja el conteo incompleto: no se dice «Ninguno» a secas.
  const partial = cut || track.isError;
  return (
    <section className="px-4 py-3" aria-label="Eventos de telemetría del período">
      <Status loading={events.isPending} error={events.isError ? events.error : null} />
      {ready ? (
        <>
          <ul className="grid gap-x-8 gap-y-1 md:grid-cols-2 xl:grid-cols-3">
            {TELEMETRY_EVENTS.map((item) => {
              const count = item.code ? power.filter((o) => o.code === item.code).length : null;
              return (
                <li key={item.name} className="flex items-baseline justify-between gap-3 border-b border-gd-line py-1.5 text-[14px]">
                  <span className="font-medium text-gd-navy">{item.name}</span>
                  <span className={count ? "font-semibold text-amber-800" : "text-gd-muted"}>
                    {count === null
                      ? "Este GPS no lo reporta"
                      : count === 0
                        ? track.isPending ? "Cargando…" : partial ? "Ninguno en lo recibido" : "Ninguno"
                        : `${partial ? "Al menos " : ""}${count} en el período`}
                  </span>
                </li>
              );
            })}
          </ul>
          {power.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {power.map((item) => (
                <li key={item.key} className="flex justify-between gap-4 text-[14px]">
                  <span className="font-medium text-gd-navy">{item.name}</span>
                  <span className="text-gd-muted">{bogotaTime(item.time)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <section aria-label="Alertas avisadas del período" className="mt-3 border-t border-gd-line pt-2">
            <h3 className="text-[13px] font-semibold text-gd-navy">Alertas avisadas</h3>
            {!alertsReady ? (
              <p className="text-[13px] text-gd-muted" role="status">
                Cargando…
              </p>
            ) : null}
            {track.isError ? (
              <p role="alert" className="text-[13px] text-amber-900">
                No se pudieron leer las posiciones del período: solo se muestran las alertas que registró el motor.
              </p>
            ) : null}
            {rules.query.isError ? (
              <p role="alert" className="text-[13px] text-amber-900">
                No se pudieron leer los avisos guardados: se muestran todas las alertas como si el aviso estuviera activo.
              </p>
            ) : null}
            {alertsReady && notified.length === 0 ? (
              <p className="text-[13px] text-gd-muted">
                {cut || track.isError ? "Ninguna alerta con aviso activo en lo recibido." : "Ninguna alerta con aviso activo en este período."} El aviso
                se enciende o se apaga en «Alertas de dispositivo».
              </p>
            ) : alertsReady ? (
              <ul className="mt-1 space-y-1">
                {notified.map((item) => (
                  <li key={item.key} className="flex justify-between gap-4 text-[14px]">
                    <span className="font-medium text-gd-navy">{item.name}</span>
                    <span className="text-gd-muted">{bogotaTime(item.time)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          {cut ? (
            <p role="status" className="mt-2 text-[13px] text-amber-900">
              Hay más datos de los que el motor entregó: acorta las fechas para ver el período completo.
            </p>
          ) : null}
          <p className="mt-2 text-[12px] text-gd-muted">
            Frenada, aceleración, giro, remolque y colisión los reportan otros modelos de GPS: el GT06 actual no los
            manda y no se calculan a partir de la velocidad.
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Alertas de dispositivo (RR-28): las alertas predeterminadas del equipo, cada una con su aviso
 * encendido o apagado, las alertas agregadas a mano y lo que llegó en el período.
 *
 * Con el aviso ENCENDIDO, la alerta aparece en «Eventos de telemetría»; apagado, no. Esto es una
 * preferencia de Drive: no le manda nada al GPS (constitución, Art. 6).
 *
 * Las alertas del período salen de los eventos del motor Y de las posiciones de tipo alarma, unidas
 * y sin duplicados: no se da por hecho que las dos fuentes coinciden.
 */
export function AlertsTab({
  row,
  dates,
  events,
  track,
  rules,
}: {
  row: RastreoRow;
  dates: PeriodControls;
  events: UseQueryResult<RastreoEventsResponse>;
  track: UseQueryResult<RastreoTrackResponse>;
  rules: AlertRulesApi;
}) {
  const blocked = Unavailable({ row, dates });
  if (blocked) return blocked;
  return <AlertsBody events={events} track={track} rules={rules} />;
}

const SWITCH_BASE =
  "inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-3 text-[13px] font-semibold disabled:opacity-60";

/**
 * El texto que se ve dentro del botón es «Aviso», que el nombre accesible («Aviso de X») contiene; el
 * estado lo da `aria-checked` y se repite afuera, como texto, para quien no distingue el color.
 * Mientras se guarda NO se deshabilita (`aria-disabled`): un botón deshabilitado pierde el foco.
 */
function NotifySwitch({
  name,
  on,
  disabled,
  busy,
  onChange,
}: {
  name: string;
  on: boolean;
  disabled: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`Aviso de ${name}`}
        disabled={disabled}
        aria-disabled={busy || undefined}
        onClick={() => {
          if (!busy) onChange(!on);
        }}
        className={`${SWITCH_BASE} ${on ? "border-gd-blue bg-gd-blue/10 text-gd-blue" : "border-gd-line bg-white text-gd-muted"}`}
      >
        <span aria-hidden="true" className={`size-2.5 rounded-full ${on ? "bg-gd-blue" : "bg-gd-line"}`} />
        Aviso
      </button>
      <span aria-hidden="true" className="text-[12px] text-gd-muted">
        {on ? "activo" : "apagado"}
      </span>
    </span>
  );
}

type Draft = { name: string; parameter: string; value: string };

function AddAlertForm({
  draft,
  focusKey,
  onDraft,
  parameters,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  draft: Draft;
  focusKey: number;
  onDraft: (draft: Draft) => void;
  parameters: string[];
  busy: boolean;
  error: string | null;
  onSubmit: (input: AddAlertRuleInput) => Promise<boolean>;
  onCancel: () => void;
}) {
  const uid = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  // Al abrirse, el foco va al primer campo; el lector de pantalla no se queda en un botón que desapareció.
  useEffect(() => nameRef.current?.focus(), [focusKey]);
  // Un valor del catálogo no se puede volver a agregar: ese código ya tiene su alerta y su aviso.
  const reserved = draft.parameter === "alarm" && isCatalogCode(draft.value.trim());
  const field = "mt-0.5 block min-h-11 w-full rounded-lg border border-gd-line bg-white px-2 text-[14px] text-gd-navy";
  const label = "block text-[12px] font-semibold text-gd-navy";
  return (
    <form
      aria-label="Agregar alerta"
      className="mb-3 grid gap-2 rounded-xl border border-gd-line bg-gd-mist p-3 sm:grid-cols-3"
      onSubmit={async (event) => {
        event.preventDefault();
        if (reserved) return;
        if (await onSubmit(draft)) onCancel();
      }}
    >
      <div>
        <label htmlFor={`${uid}-name`} className={label}>
          Nombre de la alerta
        </label>
        <input
          id={`${uid}-name`}
          ref={nameRef}
          className={field}
          value={draft.name}
          maxLength={60}
          required
          onChange={(e) => onDraft({ ...draft, name: e.target.value })}
        />
      </div>
      <div>
        <label htmlFor={`${uid}-parameter`} className={label}>
          Parámetro de la trama
        </label>
        <select id={`${uid}-parameter`} className={field} value={draft.parameter} onChange={(e) => onDraft({ ...draft, parameter: e.target.value })}>
          {(parameters.includes(draft.parameter) ? parameters : [draft.parameter, ...parameters]).map((parameter) => (
            <option key={parameter} value={parameter}>
              {parameter}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={`${uid}-value`} className={label}>
          Valor que la dispara
        </label>
        <input
          id={`${uid}-value`}
          className={field}
          value={draft.value}
          maxLength={40}
          required
          aria-invalid={reserved}
          onChange={(e) => onDraft({ ...draft, value: e.target.value })}
        />
      </div>
      <p className="text-[12px] text-gd-muted sm:col-span-3">
        Cada posición cuyo «{draft.parameter}» valga «{draft.value || "…"}» cuenta como esta alerta. Es una preferencia de Drive: no le manda nada al GPS.
      </p>
      {reserved ? (
        <p role="alert" className="text-[13px] text-amber-900 sm:col-span-3">
          «{draft.value.trim()}» ya es una alerta de la lista del dispositivo: apaga o enciende su aviso allá en vez de agregarla.
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="text-[13px] text-amber-900 sm:col-span-3">
          {error}
        </p>
      ) : null}
      <div className="flex gap-2 sm:col-span-3">
        <button type="submit" disabled={busy || reserved} className="min-h-11 rounded-lg bg-gd-blue px-3 text-[13px] font-semibold text-white disabled:opacity-60">
          Guardar alerta
        </button>
        <button type="button" onClick={onCancel} className="min-h-11 rounded-lg border border-gd-line bg-white px-3 text-[13px] font-semibold text-gd-navy">
          Cancelar
        </button>
      </div>
    </form>
  );
}

function AlertsBody({
  events,
  track,
  rules,
}: {
  events: UseQueryResult<RastreoEventsResponse>;
  track: UseQueryResult<RastreoTrackResponse>;
  rules: AlertRulesApi;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const listTitle = useRef<HTMLHeadingElement>(null);
  // Sube cuando un botón cambia el borrador con el formulario ya abierto: el foco vuelve al nombre.
  const [focusKey, setFocusKey] = useState(0);
  const points = track.data?.points;
  const occurrences = buildOccurrences(events.data, points, rules.rules).filter((item) => !item.telemetry);
  const rulesReady = !rules.query.isPending && !rules.query.isError;
  const rulesSettled = !rules.query.isPending;
  // Las alertas agregadas se evalúan con las reglas: hasta tenerlas (o saber que fallaron) no se dice
  // «Sin alertas» ni «Ninguna», y un código cubierto por una regla no parpadea como «sin catalogar».
  const loading = events.isPending || track.isPending || !rulesSettled;
  const ready = !loading && !events.isError;
  const cut = events.data?.truncated === true || track.data?.truncated === true;
  // Sin posiciones o sin reglas legibles, lo que se ve es lo recibido, no necesariamente todo.
  const partial = cut || track.isError || rules.query.isError;

  const stats = new Map<string, { count: number; last: number; lastTime: string }>();
  for (const item of occurrences) {
    const current = stats.get(item.code);
    const newer = !current || item.instant > current.last;
    stats.set(item.code, { count: (current?.count ?? 0) + 1, last: newer ? item.instant : current.last, lastTime: newer ? item.time : current.lastTime });
  }
  const catalog = Object.entries(ALERT_CATALOG).filter(([, alert]) => !alert.telemetry);
  const custom = rules.rules.filter((rule) => rule.kind === "personalizada");
  const unrecognized = [...new Map(occurrences.filter((item) => item.unrecognized).map((item) => [item.code, item])).values()];
  const parameters = frameParameters(points);
  const closeForm = () => {
    rules.clearErrors();
    setDraft(null);
    // El botón vuelve a existir al cerrar el formulario: el foco regresa a él.
    requestAnimationFrame(() => addButton.current?.focus());
  };
  const countText = (code: string) => {
    const stat = stats.get(code);
    return stat
      ? `${cut ? "Al menos " : ""}${stat.count} en el período · última ${bogotaTime(stat.lastTime)}`
      : partial
        ? "Ninguna en lo recibido"
        : "Ninguna en el período";
  };

  return (
    <section className="px-4 py-3" aria-label="Alertas de dispositivo del período">
      <Status loading={loading} error={events.isError ? events.error : null} />
      {track.isError ? (
        <p role="alert" className="mb-2 text-[13px] text-amber-900">
          No se pudieron leer las posiciones del período: solo se muestran las alertas que registró el motor.
        </p>
      ) : null}
      {rules.query.isError ? (
        <p role="alert" className="mb-2 text-[13px] text-amber-900">
          No se pudieron leer los avisos guardados: se ven como activos, no se pueden cambiar ahora y las alertas agregadas no se evaluaron.
        </p>
      ) : null}
      {rules.error && draft === null ? (
        <p role="alert" className="mb-2 text-[13px] text-amber-900">
          {rules.error}
        </p>
      ) : null}

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-gd-muted">
          Con el aviso <strong>activo</strong>, la alerta llega a «Eventos de telemetría». No le manda nada al GPS.
        </p>
        <button
          ref={addButton}
          type="button"
          disabled={draft !== null}
          onClick={() => {
            setDraft({ name: "", parameter: parameters[0] ?? "alarm", value: "" });
            setFocusKey((key) => key + 1);
          }}
          className="min-h-11 rounded-lg border border-gd-blue px-3 text-[13px] font-semibold text-gd-blue disabled:opacity-50"
        >
          Agregar alerta
        </button>
      </div>

      {draft ? (
        <AddAlertForm
          draft={draft}
          focusKey={focusKey}
          onDraft={setDraft}
          parameters={parameters}
          busy={rules.busy}
          error={rules.error}
          onSubmit={(input) => rules.add(input)}
          onCancel={closeForm}
        />
      ) : null}

      {ready ? (
        <section aria-label="Alertas recibidas en el período" className="mb-3">
          <h3 className="text-[13px] font-semibold text-gd-navy">Recibidas en el período</h3>
          {cut ? (
            <p role="status" className="mb-1 text-[13px] text-amber-900">
              Hay más datos de los que el motor entregó: acorta las fechas para ver el período completo.
            </p>
          ) : null}
          {occurrences.length === 0 ? (
            <p className="text-[14px] text-gd-muted">
              {track.isError
                ? "Sin alertas en el registro del motor; las posiciones no se pudieron leer."
                : partial
                  ? "Sin alertas en lo recibido."
                  : "Sin alertas en este período."}
            </p>
          ) : (
            <ul className="space-y-2">
              {occurrences.map((item) => (
                <li key={item.key} className="text-[14px]">
                  <p className="flex flex-wrap justify-between gap-x-4">
                    <span className="font-semibold text-gd-navy">{item.name}</span>
                    <span className="text-gd-muted">{bogotaTime(item.time)}</span>
                  </p>
                  <p className="text-[13px] text-gd-muted">{item.meaning}</p>
                  {alertGuidance(item.code) ? (
                    <p className="text-[12px] text-gd-muted">
                      <span className="font-semibold">Orientación:</span> {alertGuidance(item.code)}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {unrecognized.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {unrecognized.map((item) => (
                <li key={item.code} className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                  <span className="text-amber-900">El GPS mandó «{item.code}» y no se reconoce.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setDraft((current) => ({ name: current?.name ?? "", parameter: "alarm", value: item.code }));
                      setFocusKey((key) => key + 1);
                    }}
                    className="min-h-11 rounded-lg border border-gd-blue px-3 text-[12px] font-semibold text-gd-blue"
                  >
                    Agregar «{item.code}» como alerta
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section aria-label="Alertas del dispositivo y su aviso">
        <h3 ref={listTitle} tabIndex={-1} className="text-[13px] font-semibold text-gd-navy outline-none">
          Alertas del dispositivo
        </h3>
        <ul className="divide-y divide-gd-line">
          {catalog.map(([code, alert]) => (
            <li key={code} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-1.5">
              <div className="min-w-0 flex-1 basis-60">
                <p className="text-[14px] font-semibold text-gd-navy">{alert.name}</p>
                <p className="text-[12px] text-gd-muted">{alert.meaning}</p>
                {alertGuidance(code) ? (
                  <p className="text-[12px] text-gd-muted">
                    <span className="font-semibold">Orientación:</span> {alertGuidance(code)}
                  </p>
                ) : null}
                {ready ? <p className="text-[12px] text-gd-muted">{countText(code)}</p> : null}
              </div>
              <NotifySwitch name={alert.name} on={isNotified(code, rules.rules)} disabled={!rulesReady} busy={rules.busy} onChange={(next) => rules.setNotify(code, next)} />
            </li>
          ))}
          {custom.map((rule) => (
            <li key={rule.code} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-1.5">
              <div className="min-w-0 flex-1 basis-60">
                <p className="text-[14px] font-semibold text-gd-navy">
                  {rule.name} <span className="text-[12px] font-normal text-gd-muted">(agregada)</span>
                </p>
                <p className="text-[12px] text-gd-muted">
                  El GPS manda «{rule.value}» en «{rule.parameter}».
                </p>
                {ready ? <p className="text-[12px] text-gd-muted">{countText(rule.code)}</p> : null}
              </div>
              <div className="flex items-center gap-2">
                <NotifySwitch name={rule.name ?? rule.code} on={rule.notify} disabled={!rulesReady} busy={rules.busy} onChange={(next) => rules.setNotify(rule.code, next)} />
                <button
                  type="button"
                  disabled={!rulesReady}
                  aria-disabled={rules.busy || undefined}
                  onClick={() => {
                    if (rules.busy) return;
                    rules.remove(rule.code);
                    // La fila desaparece: el foco pasa al título de la lista en vez de perderse.
                    listTitle.current?.focus();
                  }}
                  aria-label={`Quitar ${rule.name ?? rule.code}`}
                  className="min-h-11 rounded-lg border border-gd-line px-3 text-[12px] font-semibold text-gd-muted disabled:opacity-60"
                >
                  Quitar
                </button>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[12px] text-gd-muted">
          «Orientación» es una pista de qué suele haber detrás, no lo que el GPS dijo. El significado exacto depende del modelo del GPS. Un
          código que Drive no conoce sale arriba para agregarlo con el parámetro de la trama que lo trae.
        </p>
      </section>
    </section>
  );
}

function PositionsTable({
  title,
  records,
  visible,
  track,
  chooser,
  panel,
}: {
  title: string;
  records: ReturnType<typeof toPositionRecords>;
  visible: ReadonlyArray<(typeof POSITION_FIELDS)[number]>;
  track: UseQueryResult<RastreoTrackResponse>;
  chooser: ReactNode;
  panel: ReactNode;
}) {
  return (
    <section aria-label={`Posiciones de ${title}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gd-line px-4 py-3">
        <h2 className="font-display text-base font-bold text-gd-navy">Posiciones · {title}</h2>
        <div className="flex flex-wrap items-center gap-3">
          {track.data ? (
            <p className="text-[13px] text-gd-muted">
              {records.length} punto(s){track.data.truncated ? " · recortado: acorta las fechas para verlo completo" : ""}
            </p>
          ) : null}
          {chooser}
        </div>
      </header>
      {panel}
      {track.isPending ? (
        <p className="px-4 py-6 text-[15px] text-gd-muted" role="status">
          Cargando posiciones…
        </p>
      ) : track.isError ? (
        <p className="px-4 py-6 text-[15px] text-amber-900">{errorText(track.error)}</p>
      ) : records.length === 0 ? (
        <p className="px-4 py-6 text-[15px] text-gd-muted">Sin posiciones en este período.</p>
      ) : visible.length === 0 ? (
        <p className="px-4 py-6 text-[15px] text-gd-muted">Marca al menos una columna.</p>
      ) : (
        <div className="gd-scroll-x overflow-y-auto">
          <table className="text-[14px]">
            <thead className="bg-white">
              <tr>
                {visible.map((field) => (
                  <th key={field.key} scope="col" title={field.hint}>
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id}>
                  {visible.map((field) => {
                    const text = formatPositionField(field.key, record);
                    return (
                      <td key={field.key} className={`whitespace-nowrap ${/^sin /u.test(text) ? "text-gd-muted" : "text-gd-ink"}`}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MotorHealth({ health, now }: { health: RastreoHealth; now: number }) {
  const { devices, frames, thresholds } = health;
  const stats: Array<[string, string]> = [
    ["Equipos activos", String(devices.active)],
    ["En línea", String(devices.online)],
    ["Con posición reciente", String(devices.freshTelemetry)],
    ["Tramas última hora", String(frames.lastHour)],
    ["Tramas inválidas (1 h)", String(frames.invalidLastHour)],
    ["En cuarentena (24 h)", String(frames.quarantinedLastDay)],
  ];
  // Un motor anterior no lo manda: sin el dato, no se muestra la casilla en vez de inventar un 0.
  const undecoded = frames.undecodedLastDay;
  if (undecoded !== undefined) stats.push(["Sin decodificar (24 h)", String(undecoded)]);
  return (
    <section className="border-t border-gd-line px-4 py-4" aria-label="Estado del motor">
      <h2 className="font-display text-base font-bold text-gd-navy">Estado del motor</h2>
      <p className="mt-1 text-[13px] text-gd-muted">
        «En línea» = habló en los últimos {Math.round(thresholds.connectionS / 60)} min. «Posición reciente» = menos de{" "}
        {Math.round(thresholds.freshnessS / 60)} min.
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        {stats.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-[13px] text-gd-muted">{label}</dt>
            <dd className="m-0 font-display text-2xl font-bold text-gd-navy tabular-nums">{value}</dd>
          </div>
        ))}
        <div className="col-span-2 min-w-0 sm:col-span-3">
          <dt className="text-[13px] text-gd-muted">Última trama recibida</dt>
          <dd className="m-0 font-display text-base font-bold text-gd-navy">
            {bogotaTime(frames.lastReceivedAt)} <span className="text-sm font-medium text-gd-muted">({ago(frames.lastReceivedAt, now)})</span>
          </dd>
        </div>
      </dl>
      {undecoded !== undefined && undecoded > 0 ? (
        <p role="status" className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[14px] text-amber-900">
          Hay equipos registrados que mandan tramas que el motor todavía no sabe leer. Sus posiciones pueden faltar
          o salir sin celda ni ignición. Avisa a tecnología con la placa: se agrega el tipo y se recupera lo recibido.
        </p>
      ) : null}
    </section>
  );
}
